// Procedural pixel-art textures. Every block texture is painted in code at 16x16
// and packed into a padded atlas (plus a matching glow atlas for emissive pixels).
import * as THREE from 'three';
import { RNG, valueNoise2, clamp } from './util.js';

const TS = 16;
const PAD = 8;
const CELL = TS + PAD * 2; // 32
const COLS = 16;
export const ATLAS_SIZE = CELL * COLS; // 512

export const TILES = {}; // name -> index
const tileList = []; // {name, albedo:Uint8ClampedArray, glow:Uint8ClampedArray}

class Pix {
  constructor() {
    this.a = new Uint8ClampedArray(TS * TS * 4);
    this.g = new Uint8ClampedArray(TS * TS * 4);
    for (let i = 3; i < this.g.length; i += 4) this.g[i] = 255;
  }
  set(x, y, c, alpha = 255) {
    if (x < 0 || y < 0 || x >= TS || y >= TS) return;
    const i = (y * TS + x) * 4;
    this.a[i] = c[0];
    this.a[i + 1] = c[1];
    this.a[i + 2] = c[2];
    this.a[i + 3] = alpha;
  }
  get(x, y) {
    const i = (((y + TS) % TS) * TS + ((x + TS) % TS)) * 4;
    return [this.a[i], this.a[i + 1], this.a[i + 2], this.a[i + 3]];
  }
  glow(x, y, c) {
    if (x < 0 || y < 0 || x >= TS || y >= TS) return;
    const i = (y * TS + x) * 4;
    this.g[i] = c[0];
    this.g[i + 1] = c[1];
    this.g[i + 2] = c[2];
  }
  clear(x, y) {
    this.set(x, y, [0, 0, 0], 0);
  }
  fill(c) {
    for (let y = 0; y < TS; y++) for (let x = 0; x < TS; x++) this.set(x, y, c);
  }
  // copy the albedo of every pixel into the glow buffer, scaled
  glowAll(scale = 1) {
    for (let i = 0; i < this.a.length; i += 4) {
      this.g[i] = this.a[i] * scale;
      this.g[i + 1] = this.a[i + 1] * scale;
      this.g[i + 2] = this.a[i + 2] * scale;
    }
  }
}

const mul = (c, f) => [c[0] * f, c[1] * f, c[2] * f];
const mix = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

// Paint with a palette driven by blotchy noise + per-pixel randomness
function paintNoise(p, r, pal, { scale = 4, noiseW = 0.55, seed = 1, jitter = 0.04 } = {}) {
  for (let y = 0; y < TS; y++) {
    for (let x = 0; x < TS; x++) {
      const n = valueNoise2(x / scale, y / scale, seed) * noiseW + r.next() * (1 - noiseW);
      const idx = Math.min(pal.length - 1, Math.floor(clamp(n, 0, 0.9999) * pal.length));
      p.set(x, y, mul(pal[idx], 1 + (r.next() - 0.5) * jitter));
    }
  }
}

function bevel(p, light, dark) {
  for (let i = 0; i < TS; i++) {
    p.set(i, 0, light);
    p.set(0, i, light);
    p.set(i, TS - 1, dark);
    p.set(TS - 1, i, dark);
  }
}

function addTile(name, painter, seed) {
  const p = new Pix();
  const r = new RNG(seed ?? hashStr(name));
  painter(p, r);
  TILES[name] = tileList.length;
  tileList.push({ name, albedo: p.a, glow: p.g });
}

function hashStr(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ---------- palettes ----------
const GRASS = [[66, 128, 40], [82, 150, 48], [96, 168, 56], [112, 186, 66]];
const DIRT = [[104, 68, 42], [122, 82, 52], [138, 94, 60], [150, 106, 70]];
const STONE = [[112, 112, 116], [126, 126, 130], [138, 138, 142], [152, 152, 156]];
const SAND = [[214, 198, 140], [224, 210, 154], [232, 220, 166], [240, 230, 182]];
const SNOW = [[222, 232, 244], [234, 241, 250], [244, 248, 253], [252, 253, 255]];

function dirtPainter(p, r) {
  paintNoise(p, r, DIRT, { scale: 3, seed: 7 });
  for (let i = 0; i < 5; i++) {
    const x = r.int(0, 15), y = r.int(0, 15);
    p.set(x, y, [120, 118, 112]);
  }
}

function sidePainter(topPal, depthBase, dripChance, seed) {
  return (p, r) => {
    dirtPainter(p, r);
    for (let x = 0; x < TS; x++) {
      let d = depthBase + (r.chance(0.55) ? 1 : 0) + (r.chance(dripChance) ? r.int(1, 3) : 0);
      for (let y = 0; y < d; y++) {
        const c = topPal[Math.min(topPal.length - 1, Math.floor(r.next() * topPal.length))];
        p.set(x, y, c);
      }
      p.set(x, d, mul(p.get(x, d), 0.82));
    }
  };
}

function oresPainter(cols, count = 5, glow = false) {
  return (p, r) => {
    paintNoise(p, r, STONE, { scale: 4, seed: 3 });
    for (let k = 0; k < count; k++) {
      let x = r.int(1, 13), y = r.int(1, 13);
      const n = r.int(2, 4);
      for (let i = 0; i < n; i++) {
        const c = cols[r.int(0, cols.length - 1)];
        p.set(x, y, c);
        if (glow) p.glow(x, y, mul(c, 0.6));
        if (r.chance(0.6)) {
          p.set(x + 1, y, mul(c, 0.8));
          if (glow) p.glow(x + 1, y, mul(c, 0.4));
        }
        x += r.int(-1, 1);
        y += r.int(0, 1);
      }
    }
  };
}

function neonPainter(c) {
  return (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) p.set(x, y, mul(c, 1 + (r.next() - 0.5) * 0.05 + (y < 8 ? 0.02 : -0.02)));
    for (let i = 1; i < TS - 1; i++) {
      p.set(i, 1, mix(c, [255, 255, 255], 0.3));
      p.set(1, i, mix(c, [255, 255, 255], 0.3));
      p.set(i, TS - 2, mul(c, 0.86));
      p.set(TS - 2, i, mul(c, 0.86));
    }
    for (let i = 0; i < TS; i++) {
      p.set(i, 0, mul(c, 0.7));
      p.set(0, i, mul(c, 0.7));
      p.set(i, TS - 1, mul(c, 0.62));
      p.set(TS - 1, i, mul(c, 0.62));
    }
    p.set(3, 3, mix(c, [255, 255, 255], 0.55));
    p.set(4, 3, mix(c, [255, 255, 255], 0.4));
    p.set(3, 4, mix(c, [255, 255, 255], 0.4));
  };
}

function gemBlockPainter(pal) {
  return (p, r) => {
    paintNoise(p, r, pal, { scale: 3, seed: 11, jitter: 0.03 });
    const light = mix(pal[pal.length - 1], [255, 255, 255], 0.55);
    const dark = mul(pal[0], 0.7);
    bevel(p, light, dark);
    for (let i = 2; i < 6; i++) p.set(i, 2, light);
    p.set(2, 3, light);
    p.set(2, 4, light);
    p.set(11, 12, mix(pal[2], [255, 255, 255], 0.4));
    p.set(12, 11, mix(pal[2], [255, 255, 255], 0.4));
    p.set(13, 13, dark);
  };
}

function bricksPainter(pal, mortar, bw = 8, bh = 4) {
  return (p, r) => {
    for (let y = 0; y < TS; y++) {
      const row = Math.floor(y / bh);
      for (let x = 0; x < TS; x++) {
        const off = row % 2 ? bw / 2 : 0;
        const bx = Math.floor((x + off) / bw);
        const isM = y % bh === bh - 1 || (x + off) % bw === bw - 1;
        if (isM) p.set(x, y, mul(mortar, 1 + (r.next() - 0.5) * 0.06));
        else {
          const base = pal[(bx * 7 + row * 3) % pal.length];
          p.set(x, y, mul(base, 1 + (r.next() - 0.5) * 0.1 + (y % bh === 0 ? 0.08 : 0)));
        }
      }
    }
  };
}

function plankPainter(pal, line) {
  return (p, r) => {
    for (let y = 0; y < TS; y++) {
      const board = Math.floor(y / 4);
      const seam = (board * 5 + 3) % TS;
      for (let x = 0; x < TS; x++) {
        let c = pal[(board + (x > seam ? 1 : 0)) % pal.length];
        c = mul(c, 1 + (valueNoise2(x / 5, y * 2, board) - 0.5) * 0.12 + (r.next() - 0.5) * 0.04);
        if (y % 4 === 3) c = line;
        else if (x === seam) c = mul(line, 1.1);
        p.set(x, y, c);
      }
    }
  };
}

function logSidePainter(pal, dark) {
  return (p, r) => {
    for (let x = 0; x < TS; x++) {
      const colShade = r.next();
      for (let y = 0; y < TS; y++) {
        let c = pal[Math.floor(clamp(colShade * 0.7 + r.next() * 0.3, 0, 0.999) * pal.length)];
        if ((x % 4 === 1 && r.chance(0.7)) || r.chance(0.05)) c = dark;
        p.set(x, y, c);
      }
    }
  };
}

function logTopPainter(bark, inner, ring) {
  return (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        const dx = x - 7.5, dy = y - 7.5;
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        let c;
        if (d > 6.5) c = bark[r.int(0, bark.length - 1)];
        else c = Math.floor(d) % 2 === 0 ? inner : ring;
        p.set(x, y, mul(c, 1 + (r.next() - 0.5) * 0.06));
      }
  };
}

function leavesPainter(pal, holes = 0.12, snow = 0) {
  return (p, r) => {
    paintNoise(p, r, pal, { scale: 2.5, seed: 5, jitter: 0.06 });
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        if (snow > 0 && r.chance(snow * (1 - y / TS))) p.set(x, y, SNOW[r.int(1, 3)]);
        if (r.chance(holes)) p.clear(x, y);
      }
  };
}

function crossPainter(fn) {
  return (p, r) => {
    for (let y = 0; y < TS; y++) for (let x = 0; x < TS; x++) p.clear(x, y);
    fn(p, r);
  };
}

const QMARK = ['.####.', '##..##', '....##', '...##.', '..##..', '..##..', '......', '..##..'];

// ---------------- tile definitions ----------------
function defineTiles() {
  addTile('grass_top', (p, r) => {
    paintNoise(p, r, GRASS, { scale: 3, seed: 2, jitter: 0.06 });
    for (let i = 0; i < 6; i++) p.set(r.int(0, 15), r.int(0, 15), [130, 200, 80]);
  });
  addTile('grass_side', sidePainter(GRASS, 3, 0.35));
  addTile('dirt', dirtPainter);
  addTile('stone', (p, r) => {
    paintNoise(p, r, STONE, { scale: 4, seed: 3 });
    for (let i = 0; i < 4; i++) {
      const x = r.int(0, 14), y = r.int(0, 14);
      p.set(x, y, [100, 100, 104]);
      p.set(x + 1, y, [104, 104, 108]);
    }
  });
  addTile('cobble', (p, r) => {
    const pts = [];
    for (let i = 0; i < 9; i++) pts.push([r.range(0, 16), r.range(0, 16), r.next()]);
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        let d1 = 1e9, d2 = 1e9, sh = 0;
        for (const [px, py, s] of pts) {
          for (let ox = -16; ox <= 16; ox += 16)
            for (let oy = -16; oy <= 16; oy += 16) {
              const d = Math.hypot(x + 0.5 - px - ox, y + 0.5 - py - oy);
              if (d < d1) {
                d2 = d1;
                d1 = d;
                sh = s;
              } else if (d < d2) d2 = d;
            }
        }
        let c = d2 - d1 < 1.1 ? [84, 84, 88] : STONE[Math.floor(sh * 4)];
        p.set(x, y, mul(c, 1 + (r.next() - 0.5) * 0.08));
      }
  });
  addTile('planks', plankPainter([[184, 142, 88], [170, 128, 76], [196, 154, 98]], [118, 86, 50]));
  addTile('dark_planks', plankPainter([[112, 80, 50], [98, 69, 42], [126, 92, 58]], [64, 44, 26]));
  addTile('log_side', logSidePainter([[104, 77, 45], [92, 67, 38], [118, 88, 52]], [74, 54, 30]));
  addTile('log_top', logTopPainter([[104, 77, 45], [92, 67, 38]], [190, 148, 92], [160, 120, 70]));
  addTile('leaves', leavesPainter([[44, 108, 34], [56, 126, 42], [70, 146, 52], [86, 162, 62]], 0.12));
  addTile('birch_side', (p, r) => {
    paintNoise(p, r, [[214, 212, 200], [226, 224, 214], [238, 236, 228]], { scale: 3, seed: 9 });
    for (let k = 0; k < 7; k++) {
      const y = r.int(0, 15), x = r.int(0, 12), len = r.int(1, 4);
      for (let i = 0; i < len; i++) p.set(x + i, y, r.chance(0.5) ? [40, 40, 42] : [70, 70, 72]);
    }
  });
  addTile('birch_top', logTopPainter([[226, 224, 214], [200, 198, 186]], [214, 186, 132], [190, 160, 108]));
  addTile('birch_leaves', leavesPainter([[92, 152, 58], [106, 170, 70], [120, 186, 82], [84, 138, 52]], 0.12));
  addTile('pine_side', logSidePainter([[78, 54, 32], [66, 45, 27], [90, 63, 38]], [52, 36, 20]));
  addTile('pine_top', logTopPainter([[78, 54, 32], [66, 45, 27]], [150, 112, 70], [126, 92, 56]));
  addTile('pine_leaves', leavesPainter([[30, 74, 46], [38, 90, 56], [48, 104, 64], [26, 64, 40]], 0.1));
  addTile('snow_pine_leaves', leavesPainter([[30, 74, 46], [38, 90, 56], [48, 104, 64], [26, 64, 40]], 0.08, 0.7));
  addTile('sand', (p, r) => {
    paintNoise(p, r, SAND, { scale: 3, seed: 4, jitter: 0.03 });
    for (let i = 0; i < 6; i++) p.set(r.int(0, 15), r.int(0, 15), [200, 182, 126]);
  });
  addTile('red_sand', (p, r) => paintNoise(p, r, [[184, 98, 48], [196, 110, 56], [206, 122, 66], [214, 134, 76]], { scale: 3, seed: 4 }));
  addTile('sandstone_side', (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        let c;
        if (y < 3) c = [236, 224, 176];
        else if (y === 3 || y === 12) c = [196, 178, 126];
        else if (y < 12) c = [224, 208, 154];
        else c = [214, 196, 142];
        if (y > 4 && y < 11 && (x + y * 3) % 9 === 0) c = [206, 190, 136];
        p.set(x, y, mul(c, 1 + (r.next() - 0.5) * 0.05));
      }
  });
  addTile('sandstone_top', (p, r) => {
    paintNoise(p, r, [[226, 212, 160], [232, 220, 168], [238, 226, 176]], { scale: 4, seed: 8, jitter: 0.02 });
    bevel(p, [240, 232, 190], [206, 190, 138]);
  });
  addTile('chiseled_sandstone', (p, r) => {
    paintNoise(p, r, [[226, 210, 156], [232, 218, 164]], { scale: 4, seed: 8, jitter: 0.02 });
    bevel(p, [240, 232, 190], [196, 178, 126]);
    // little ankh / eye glyph
    const g = [176, 150, 96];
    for (let y = 4; y < 12; y++) p.set(7, y, g), p.set(8, y, g);
    for (let x = 5; x < 11; x++) p.set(x, 6, g);
    p.set(6, 3, g), p.set(9, 3, g), p.set(7, 2, g), p.set(8, 2, g);
  });
  addTile('cactus_side', (p, r) => {
    paintNoise(p, r, [[56, 132, 48], [64, 146, 54], [74, 158, 62]], { scale: 3, seed: 6 });
    for (const sx of [2, 7, 12])
      for (let y = 0; y < TS; y++) {
        p.set(sx, y, [40, 100, 36]);
        if (y % 4 === 1) p.set(sx + 1, y, [214, 236, 180]);
      }
    for (let y = 0; y < TS; y++) (p.set(0, y, [36, 90, 32]), p.set(15, y, [36, 90, 32]));
  });
  addTile('cactus_top', (p, r) => {
    paintNoise(p, r, [[64, 146, 54], [74, 158, 62], [84, 170, 70]], { scale: 3, seed: 6 });
    bevel(p, [36, 90, 32], [36, 90, 32]);
    for (let i = 5; i < 11; i++) (p.set(i, 7, [100, 186, 84]), p.set(7, i, [100, 186, 84]));
  });
  addTile('snow', (p, r) => paintNoise(p, r, SNOW, { scale: 3, seed: 12, jitter: 0.02 }));
  addTile('snow_side', sidePainter(SNOW, 4, 0.4));
  addTile('ice', (p, r) => {
    paintNoise(p, r, [[150, 200, 245], [160, 210, 250], [172, 218, 252]], { scale: 5, seed: 13, jitter: 0.02 });
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) if ((x + y) % 9 === 0 && y < 12 && r.chance(0.75)) p.set(x, y, [228, 244, 255]);
    bevel(p, [196, 230, 255], [128, 180, 230]);
  });
  addTile('packed_ice', (p, r) => {
    paintNoise(p, r, [[122, 170, 228], [132, 182, 236], [142, 192, 242]], { scale: 4, seed: 14 });
    let x = 2, y = 3;
    for (let i = 0; i < 14; i++) {
      p.set(x, y, [200, 230, 255]);
      x += r.int(0, 1);
      y += r.int(0, 1);
    }
  });
  addTile('gold_block', gemBlockPainter([[226, 168, 40], [240, 190, 56], [248, 206, 80], [254, 222, 110]]));
  addTile('diamond_block', gemBlockPainter([[44, 190, 196], [64, 214, 218], [96, 230, 230], [140, 244, 240]]));
  addTile('emerald_block', gemBlockPainter([[34, 168, 84], [48, 192, 100], [70, 214, 120], [110, 232, 150]]));
  addTile('ruby_block', gemBlockPainter([[180, 30, 50], [204, 44, 64], [226, 66, 84], [244, 104, 116]]));
  addTile('brick', bricksPainter([[168, 72, 58], [152, 62, 50], [182, 84, 66]], [196, 186, 172]));
  addTile('stone_brick', bricksPainter([[128, 128, 132], [120, 120, 124], [138, 138, 142]], [88, 88, 92], 8, 8));
  addTile('glass', (p, r) => {
    for (let y = 0; y < TS; y++) for (let x = 0; x < TS; x++) p.clear(x, y);
    for (let i = 0; i < TS; i++) {
      p.set(i, 0, [214, 238, 250]);
      p.set(0, i, [214, 238, 250]);
      p.set(i, 15, [170, 210, 232]);
      p.set(15, i, [170, 210, 232]);
    }
    for (let i = 0; i < 4; i++) (p.set(3 + i, 6 - i, [255, 255, 255]), p.set(4 + i, 8 - i, [235, 248, 255]));
  });
  addTile('glowstone', (p, r) => {
    const pal = [[214, 150, 60], [248, 190, 80], [255, 224, 128], [255, 244, 196]];
    paintNoise(p, r, pal, { scale: 2, seed: 15, noiseW: 0.7 });
    for (let i = 0; i < 10; i++) {
      const x = r.int(0, 15), y = r.int(0, 15);
      p.set(x, y, [150, 100, 50]);
    }
    p.glowAll(0.9);
  });
  addTile('lamp', (p, r) => {
    p.fill([60, 50, 40]);
    for (let y = 2; y < 14; y++) for (let x = 2; x < 14; x++) p.set(x, y, mix([255, 200, 100], [255, 240, 190], r.next()));
    for (let i = 0; i < TS; i++) (p.set(i, 7, [70, 60, 50]), p.set(7, i, [70, 60, 50]));
    for (let y = 2; y < 14; y++) for (let x = 2; x < 14; x++) if (x !== 7 && y !== 7) p.glow(x, y, mul(p.get(x, y), 0.95));
  });
  addTile('obsidian', (p, r) => {
    paintNoise(p, r, [[18, 12, 30], [24, 16, 40], [32, 22, 52], [44, 28, 70]], { scale: 3, seed: 16, noiseW: 0.4 });
    for (let i = 0; i < 6; i++) p.set(r.int(0, 15), r.int(0, 15), [96, 64, 150]);
  });
  addTile('magma_rock', (p, r) => paintNoise(p, r, [[84, 28, 26], [104, 38, 34], [122, 48, 42], [140, 60, 50]], { scale: 2.5, seed: 17 }));
  addTile('magma', (p, r) => {
    paintNoise(p, r, [[52, 16, 12], [66, 22, 16], [80, 28, 20]], { scale: 3, seed: 18 });
    for (let k = 0; k < 4; k++) {
      let x = r.int(0, 15), y = r.int(0, 15);
      for (let i = 0; i < 9; i++) {
        const c = r.chance(0.4) ? [255, 210, 80] : [255, 130, 30];
        p.set(x, y, c);
        p.glow(x, y, c);
        x = (x + r.int(-1, 1) + 16) % 16;
        y = (y + r.int(-1, 1) + 16) % 16;
      }
    }
  });
  addTile('basalt', (p, r) => logSidePainter([[56, 56, 64], [64, 64, 72], [74, 74, 82]], [44, 44, 50])(p, r));
  addTile('crystal', (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        const band = Math.floor((x + y) / 4) % 4;
        const pal = [[150, 86, 230], [176, 112, 255], [204, 156, 255], [128, 70, 210]];
        p.set(x, y, mul(pal[band], 1 + (r.next() - 0.5) * 0.08));
      }
    bevel(p, [232, 210, 255], [96, 50, 170]);
    p.glowAll(0.75);
  });
  addTile('glitch', (p, r) => {
    const pal = [[255, 43, 214], [34, 232, 255], [18, 8, 24], [91, 26, 143], [18, 8, 24], [30, 12, 40]];
    for (let y = 0; y < TS; y += 2)
      for (let x = 0; x < TS; x += 2) {
        const c = pal[r.int(0, pal.length - 1)];
        for (let dy = 0; dy < 2; dy++)
          for (let dx = 0; dx < 2; dx++) {
            p.set(x + dx, y + dy, c);
            if (c[0] + c[1] + c[2] > 250) p.glow(x + dx, y + dy, mul(c, 0.8));
          }
      }
  });
  addTile('void_stone', (p, r) => {
    paintNoise(p, r, [[205, 202, 155], [220, 218, 170], [233, 231, 184], [242, 240, 200]], { scale: 3, seed: 19 });
    for (let i = 0; i < 6; i++) p.set(r.int(0, 15), r.int(0, 15), [180, 176, 128]);
  });
  addTile('void_brick', bricksPainter([[167, 123, 202], [152, 110, 188], [180, 138, 214]], [118, 82, 148], 8, 8));
  addTile('coal_ore', oresPainter([[34, 34, 36], [56, 56, 60]]));
  addTile('iron_ore', oresPainter([[214, 172, 142], [232, 196, 168]]));
  addTile('gold_ore', oresPainter([[250, 214, 64], [255, 240, 140]], 5, true));
  addTile('diamond_ore', oresPainter([[84, 236, 236], [180, 255, 255]], 5, true));
  addTile('ruby_ore', oresPainter([[230, 40, 60], [255, 110, 120]], 5, true));
  addTile('bounce', (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        const d = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
        let c = d > 6.5 ? [70, 170, 54] : d > 4.5 ? [120, 226, 88] : d > 2.5 ? [100, 206, 74] : [150, 240, 120];
        p.set(x, y, mul(c, 1 + (r.next() - 0.5) * 0.05));
      }
    p.set(4, 4, [220, 255, 210]);
    p.set(5, 4, [200, 255, 190]);
    p.glowAll(0.25);
  });
  addTile('bounce_side', (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        let c = y < 4 ? [120, 226, 88] : y < 6 ? [70, 170, 54] : (x + y) % 4 === 0 ? [60, 60, 64] : [80, 80, 86];
        p.set(x, y, mul(c, 1 + (r.next() - 0.5) * 0.05));
      }
    for (let x = 0; x < TS; x++) p.glow(x, 1, [40, 90, 30]);
  });
  addTile('speed', (p, r) => {
    p.fill([40, 44, 54]);
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        const k = (y + Math.abs(x - 7.5) * 1.0) % 8;
        if (k < 3 && x > 1 && x < 14) {
          p.set(x, y, [255, 210, 40]);
          p.glow(x, y, [200, 150, 20]);
        }
      }
    bevel(p, [80, 86, 100], [24, 26, 32]);
  });
  addTile('marble', (p, r) => {
    paintNoise(p, r, [[228, 224, 216], [236, 232, 226], [244, 241, 236]], { scale: 4, seed: 20, jitter: 0.02 });
    let x = 0, y = r.int(2, 12);
    for (let i = 0; i < 20; i++) {
      p.set(x, y, [200, 196, 190]);
      x++;
      y += r.int(-1, 1);
    }
    bevel(p, [250, 248, 244], [200, 196, 188]);
  });
  addTile('path', (p, r) => paintNoise(p, r, [[146, 114, 70], [160, 128, 82], [172, 140, 92], [184, 152, 102]], { scale: 3, seed: 21 }));
  addTile('pumpkin_side', (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        let c = x % 4 === 0 ? [196, 104, 20] : [228, 136, 32];
        p.set(x, y, mul(c, 1 + (r.next() - 0.5) * 0.08));
      }
  });
  addTile('pumpkin_top', (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) p.set(x, y, mul((x + y) % 5 === 0 ? [200, 108, 22] : [226, 134, 30], 1 + (r.next() - 0.5) * 0.06));
    for (let y = 6; y < 10; y++) for (let x = 6; x < 10; x++) p.set(x, y, [86, 120, 40]);
  });
  addTile('jack_face', (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) p.set(x, y, mul(x % 4 === 0 ? [196, 104, 20] : [228, 136, 32], 1 + (r.next() - 0.5) * 0.08));
    const glow = [255, 220, 90];
    const holes = [
      [3, 4], [4, 4], [3, 5], [4, 5], [5, 5], [10, 4], [11, 4], [10, 5], [11, 5], [12, 5],
    ];
    for (let x = 3; x < 13; x++) {
      p.set(x, 10, glow);
      if (x % 3 !== 0) p.set(x, 11, glow);
    }
    for (const [x, y] of holes) p.set(x, y, glow);
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        const c = p.get(x, y);
        if (c[0] === 255) p.glow(x, y, [255, 180, 60]);
      }
  });
  addTile('lucky', (p, r) => {
    for (let y = 0; y < TS; y++) for (let x = 0; x < TS; x++) p.set(x, y, mul([255, 204, 51], 1 + (r.next() - 0.5) * 0.06));
    bevel(p, [255, 236, 140], [196, 130, 20]);
    for (let i = 1; i < 15; i++) (p.set(i, 1, [255, 226, 110]), p.set(1, i, [255, 226, 110]), p.set(i, 14, [214, 150, 30]), p.set(14, i, [214, 150, 30]));
    for (let y = 0; y < QMARK.length; y++)
      for (let x = 0; x < 6; x++)
        if (QMARK[y][x] === '#') {
          p.set(5 + x, 4 + y, [255, 255, 255]);
          p.set(6 + x, 5 + y, [150, 90, 10]);
        }
    for (let y = 0; y < QMARK.length; y++) for (let x = 0; x < 6; x++) if (QMARK[y][x] === '#') p.set(5 + x, 4 + y, [255, 255, 255]);
    p.glowAll(0.35);
  });
  addTile('brain', (p, r) => {
    for (let y = 0; y < TS; y++) for (let x = 0; x < TS; x++) p.set(x, y, mul([150, 80, 255], 1 + (r.next() - 0.5) * 0.08));
    bevel(p, [210, 170, 255], [90, 40, 170]);
    for (let y = 0; y < QMARK.length; y++)
      for (let x = 0; x < 6; x++)
        if (QMARK[y][x] === '#') {
          p.set(6 + x, 5 + y, [70, 20, 130]);
        }
    for (let y = 0; y < QMARK.length; y++) for (let x = 0; x < 6; x++) if (QMARK[y][x] === '#') p.set(5 + x, 4 + y, [255, 255, 255]);
    p.glowAll(0.4);
  });
  addTile('chest_front', (p, r) => {
    plankPainter([[160, 110, 50], [148, 100, 44], [170, 120, 58]], [96, 62, 28])(p, r);
    bevel(p, [70, 44, 20], [60, 38, 16]);
    for (let x = 1; x < 15; x++) p.set(x, 5, [70, 44, 20]);
    for (let y = 4; y < 9; y++) for (let x = 7; x < 9; x++) p.set(x, y, [230, 200, 80]);
    p.set(7, 7, [60, 40, 10]);
  });
  addTile('chest_side', (p, r) => {
    plankPainter([[160, 110, 50], [148, 100, 44], [170, 120, 58]], [96, 62, 28])(p, r);
    bevel(p, [70, 44, 20], [60, 38, 16]);
    for (let x = 1; x < 15; x++) p.set(x, 5, [70, 44, 20]);
  });
  addTile('chest_top', (p, r) => {
    plankPainter([[160, 110, 50], [148, 100, 44], [170, 120, 58]], [96, 62, 28])(p, r);
    bevel(p, [70, 44, 20], [60, 38, 16]);
  });
  addTile('checkpoint', (p, r) => {
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        const d = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
        p.set(x, y, mul(d > 6.5 ? [60, 64, 74] : d > 5.5 ? [120, 230, 120] : [86, 92, 104], 1 + (r.next() - 0.5) * 0.05));
      }
    for (let y = 0; y < TS; y++)
      for (let x = 0; x < TS; x++) {
        const d = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
        if (d > 5.5 && d <= 6.5) p.glow(x, y, [60, 180, 60]);
      }
  });
  addTile('cloud', (p, r) => paintNoise(p, r, [[236, 240, 248], [244, 247, 252], [252, 253, 255]], { scale: 4, seed: 22, jitter: 0.01 }));
  addTile('bookshelf', (p, r) => {
    plankPainter([[184, 142, 88], [170, 128, 76]], [118, 86, 50])(p, r);
    const cols = [[180, 50, 50], [50, 90, 180], [60, 150, 70], [200, 170, 60], [140, 70, 160]];
    for (const yb of [2, 9])
      for (let x = 1; x < 15; x++) {
        const c = cols[(x * 7 + yb) % cols.length];
        for (let y = yb; y < yb + 5; y++) p.set(x, y, mul(c, x % 2 ? 1 : 0.85));
      }
  });

  addTile('furnace', (p,r)=>{paintNoise(p,r,[[85,94,101],[110,118,125]]);for(let y=7;y<14;y++)for(let x=3;x<13;x++)p.set(x,y,y>10?[230,110,33]:[35,39,48]);});
  addTile('bed',(p)=>{p.fill([188,58,65]);for(let y=0;y<5;y++)for(let x=1;x<15;x++)p.set(x,y,[249,240,215]);bevel(p,[232,95,102],[119,40,47]);});
  for(const powered of [false,true])addTile(powered?'powered_rail':'rail',(p)=>{p.fill([94,66,42]);for(let y=0;y<16;y++)for(let x=0;x<16;x++){if(x===3||x===4||x===11||x===12)p.set(x,y,powered?[250,196,66]:[184,198,212]);else if(y%5<2)p.set(x,y,[143,99,55]);}if(powered)p.glowAll(.2);});
  addTile('craft_top', (p, r) => {
    plankPainter([[184,142,88],[170,128,76]], [118,86,50])(p,r);
    for(let y=1;y<15;y++) for(let x=1;x<15;x++) if(x===1||x===5||x===10||x===14||y===1||y===5||y===10||y===14) p.set(x,y,[73,47,29]);
  });
  addTile('craft_side', (p,r) => {
    plankPainter([[162,109,60],[149,98,52]], [90,58,32])(p,r);
    for(let y=3;y<14;y++) { p.set(5,y,[72,46,29]); p.set(11,y,[72,46,29]); }
    for(let x=2;x<9;x++) for(let y=3;y<5;y++) p.set(x,y,[192,206,215]);
    for(let x=10;x<14;x++) for(let y=3;y<7;y++) p.set(x,y,[190,203,211]);
  });

  // neon obby blocks
  const NEON = {
    neon_red: [236, 64, 64],
    neon_orange: [255, 140, 40],
    neon_yellow: [255, 214, 50],
    neon_lime: [120, 220, 60],
    neon_cyan: [40, 210, 230],
    neon_blue: [60, 110, 240],
    neon_purple: [160, 80, 240],
    neon_pink: [255, 100, 190],
    neon_white: [236, 238, 244],
    neon_black: [40, 42, 52],
  };
  for (const [k, c] of Object.entries(NEON)) addTile(k, neonPainter(c));

  // cross sprites (plants)
  addTile('flower_red', crossPainter((p, r) => flower(p, r, [220, 44, 44], [255, 220, 60])));
  addTile('flower_yellow', crossPainter((p, r) => flower(p, r, [250, 214, 50], [230, 120, 20])));
  addTile('flower_blue', crossPainter((p, r) => flower(p, r, [80, 150, 250], [255, 255, 255])));
  addTile('tall_grass', crossPainter((p, r) => {
    for (const bx of [2, 4, 6, 8, 10, 12, 13]) {
      const h = r.int(5, 11);
      let x = bx;
      for (let i = 0; i < h; i++) {
        p.set(Math.round(x), 15 - i, GRASS[Math.min(3, Math.floor((i / h) * 4))]);
        x += (bx - 7.5) * 0.04;
      }
    }
  }));
  addTile('dead_bush', crossPainter((p, r) => {
    const c = [134, 96, 48];
    for (let i = 0; i < 8; i++) p.set(7, 15 - i, c);
    for (let i = 0; i < 5; i++) (p.set(7 - i, 11 - i, c), p.set(8 + i, 10 - i, c));
    for (let i = 0; i < 3; i++) (p.set(4 - i, 8, c), p.set(11 + i, 7 - i, c));
  }));
  addTile('mushroom', crossPainter((p, r) => {
    for (let y = 9; y < 16; y++) (p.set(7, y, [236, 226, 210]), p.set(8, y, [220, 210, 196]));
    for (let y = 5; y < 10; y++) for (let x = 4 + (y === 5 ? 1 : 0); x < 12 - (y === 5 ? 1 : 0); x++) p.set(x, y, [210, 40, 40]);
    p.set(6, 6, [255, 255, 255]), p.set(9, 7, [255, 255, 255]), p.set(5, 8, [255, 255, 255]), p.set(10, 6, [255, 255, 255]);
  }));
  addTile('crystal_shard', crossPainter((p, r) => {
    const cols = [[176, 112, 255], [214, 170, 255], [140, 80, 230]];
    for (const [bx, h] of [[4, 8], [7, 13], [10, 9], [12, 6]]) {
      for (let i = 0; i < h; i++) {
        const w = i < h - 2 ? 1 : 0;
        for (let dx = -w; dx <= w; dx++) {
          const c = cols[(i + dx + 3) % 3];
          p.set(bx + dx, 15 - i, c);
          p.glow(bx + dx, 15 - i, mul(c, 0.8));
        }
      }
    }
  }));
  addTile('snow_bush', crossPainter((p, r) => {
    for (let k = 0; k < 40; k++) {
      const x = r.int(3, 12), y = r.int(7, 15);
      p.set(x, y, r.chance(0.5) ? [30, 74, 46] : SNOW[r.int(1, 3)]);
    }
  }));
  addTile('fire_flower', crossPainter((p, r) => {
    for (let y = 9; y < 16; y++) p.set(7, y, [70, 50, 40]);
    const cols = [[255, 120, 30], [255, 200, 60], [255, 80, 20]];
    for (let k = 0; k < 18; k++) {
      const x = r.int(4, 11), y = r.int(2, 9);
      const c = cols[r.int(0, 2)];
      p.set(x, y, c);
      p.glow(x, y, c);
    }
  }));
}

function flower(p, r, petal, center) {
  const stem = [64, 140, 44];
  for (let y = 8; y < 16; y++) p.set(7, y, stem);
  p.set(6, 12, stem), p.set(5, 11, stem), p.set(8, 13, stem), p.set(9, 12, stem);
  for (const [dx, dy] of [[0, -1], [-1, 0], [1, 0], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]]) p.set(7 + dx, 6 + dy, mul(petal, dx && dy ? 0.85 : 1));
  p.set(7, 4, petal), p.set(5, 6, petal), p.set(9, 6, petal);
  p.set(7, 6, center);
}

// ------------- atlas assembly -------------
let atlasTex = null, glowTex = null;
export let atlasCanvas = null;

export function buildAtlas(renderer) {
  defineTiles();
  atlasCanvas = document.createElement('canvas');
  atlasCanvas.width = atlasCanvas.height = ATLAS_SIZE;
  const glowCanvas = document.createElement('canvas');
  glowCanvas.width = glowCanvas.height = ATLAS_SIZE;
  const actx = atlasCanvas.getContext('2d');
  const gctx = glowCanvas.getContext('2d');
  gctx.fillStyle = '#000';
  gctx.fillRect(0, 0, ATLAS_SIZE, ATLAS_SIZE);
  const tmp = document.createElement('canvas');
  tmp.width = tmp.height = TS;
  const tctx = tmp.getContext('2d');
  const cellC = document.createElement('canvas');
  cellC.width = cellC.height = CELL;
  const cctx = cellC.getContext('2d');

  const blit = (data, ctx, i) => {
    tctx.putImageData(new ImageData(new Uint8ClampedArray(data), TS, TS), 0, 0);
    cctx.clearRect(0, 0, CELL, CELL);
    for (let oy = -1; oy <= 1; oy++) for (let ox = -1; ox <= 1; ox++) cctx.drawImage(tmp, PAD + ox * TS, PAD + oy * TS);
    const cx = (i % COLS) * CELL, cy = Math.floor(i / COLS) * CELL;
    ctx.drawImage(cellC, cx, cy);
  };
  tileList.forEach((t, i) => {
    blit(t.albedo, actx, i);
    blit(t.glow, gctx, i);
  });

  const mk = (cv) => {
    const t = new THREE.CanvasTexture(cv);
    t.colorSpace = THREE.SRGBColorSpace;
    t.magFilter = THREE.NearestFilter;
    t.minFilter = THREE.NearestMipmapLinearFilter;
    t.generateMipmaps = true;
    t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
    return t;
  };
  atlasTex = mk(atlasCanvas);
  glowTex = mk(glowCanvas);
  return { atlas: atlasTex, glow: glowTex };
}

// UV rect for a tile (in 0..1 texture space, v up)
export function tileUV(name) {
  const i = typeof name === 'number' ? name : TILES[name];
  if (i === undefined) throw new Error('Unknown tile ' + name);
  const cx = (i % COLS) * CELL + PAD, cy = Math.floor(i / COLS) * CELL + PAD;
  const e = 0.02;
  return {
    u0: (cx + e) / ATLAS_SIZE,
    u1: (cx + TS - e) / ATLAS_SIZE,
    v0: 1 - (cy + TS - e) / ATLAS_SIZE,
    v1: 1 - (cy + e) / ATLAS_SIZE,
  };
}

// A CSS data URL of a tile, scaled up for crisp pixel UI backgrounds
export function tileDataURL(name, scale = 4) {
  const t = tileList[TILES[name]];
  if (!t) return '';
  const c = document.createElement('canvas');
  c.width = c.height = TS;
  c.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(t.albedo), TS, TS), 0, 0);
  const big = document.createElement('canvas');
  big.width = big.height = TS * scale;
  const bctx = big.getContext('2d');
  bctx.imageSmoothingEnabled = false;
  bctx.drawImage(c, 0, 0, TS * scale, TS * scale);
  return big.toDataURL();
}

export function tileCanvas(name) {
  const t = tileList[TILES[name]];
  const c = document.createElement('canvas');
  c.width = c.height = TS;
  c.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(t.albedo), TS, TS), 0, 0);
  return c;
}

// ---------- liquid textures (tileable, animated by scrolling) ----------
export function makeWaterTexture() {
  const S = 32;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(S, S);
  const r = new RNG(99);
  for (let y = 0; y < S; y++)
    for (let x = 0; x < S; x++) {
      const n = valueNoise2(x / 6, y / 3, 3) * 0.6 + Math.sin((x + y * 0.5) / 3) * 0.2 + r.next() * 0.2;
      const t = clamp(n, 0, 1);
      const col = mix([40, 110, 210], [120, 190, 255], t);
      const i = (y * S + x) * 4;
      img.data[i] = col[0];
      img.data[i + 1] = col[1];
      img.data[i + 2] = col[2];
      img.data[i + 3] = 255;
      if (t > 0.78) (img.data[i] = 200), (img.data[i + 1] = 235), (img.data[i + 2] = 255);
    }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function makeLavaTexture() {
  const S = 32;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(S, S);
  const r = new RNG(77);
  for (let y = 0; y < S; y++)
    for (let x = 0; x < S; x++) {
      const n = valueNoise2(x / 5, y / 5, 8) * 0.7 + r.next() * 0.3;
      let col;
      if (n > 0.72) col = [255, 236, 120];
      else if (n > 0.5) col = [255, 170, 40];
      else if (n > 0.3) col = [240, 100, 20];
      else col = [190, 50, 14];
      const i = (y * S + x) * 4;
      img.data.set([col[0], col[1], col[2], 255], i);
    }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// ---------- pixel sprites for held items ----------
export const SWORD_SHAPE = [
  '.............###',
  '............#wl#',
  '...........#wwl#',
  '..........#wwl#.',
  '.........#wwl#..',
  '........#wwl#...',
  '.......#wwl#....',
  '..##..#wwl#.....',
  '..#g##wwl#......',
  '...#ggwl#.......',
  '...##gg#........',
  '..#hh#gg#.......',
  '.#hh#.##........',
  '#hh#............',
  '#p#.............',
  '###.............',
];

// ---------- misc canvases ----------
export function makeCoinTexture() {
  const S = 64;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  // pixel art coin at 16x16 then upscale
  const p = document.createElement('canvas');
  p.width = p.height = 16;
  const pc = p.getContext('2d');
  const img = pc.createImageData(16, 16);
  const S_LETTER = ['.####.', '##..##', '##....', '.####.', '....##', '##..##', '.####.'];
  for (let y = 0; y < 16; y++)
    for (let x = 0; x < 16; x++) {
      const d = Math.hypot(x - 7.5, y - 7.5);
      let col = null;
      if (d < 7.6) col = d > 6.4 ? [190, 120, 10] : d > 5.4 ? [255, 214, 70] : [250, 190, 40];
      if (col && d <= 5.4 && x + y < 11) col = [255, 226, 110];
      const i = (y * 16 + x) * 4;
      if (col) img.data.set([col[0], col[1], col[2], 255], i);
    }
  for (let y = 0; y < 7; y++)
    for (let x = 0; x < 6; x++)
      if (S_LETTER[y][x] === '#') {
        const i = ((y + 4) * 16 + (x + 5)) * 4;
        img.data.set([160, 96, 6, 255], i);
        const j = ((y + 4) * 16 + (x + 5) - 1) * 4;
        if (S_LETTER[y][x - 1] !== '#') img.data.set([255, 240, 160, 255], j);
      }
  pc.putImageData(img, 0, 0);
  ctx.drawImage(p, 0, 0, S, S);
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// Big readable label for answer pillars / blitz blocks
export function makeLabelTexture(text, { bg = '#2b2f45', fg = '#ececec', border = '#ffd23f', w = 256, h = 256, font = 'Lilita One' } = {}) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);
  ctx.lineWidth = 16;
  ctx.strokeStyle = border;
  ctx.strokeRect(8, 8, w - 16, h - 16);
  let size = Math.floor(h * 0.55);
  ctx.font = `${size}px "${font}", "Arial Black", sans-serif`;
  while (ctx.measureText(text).width > w * 0.82 && size > 20) {
    size -= 4;
    ctx.font = `${size}px "${font}", "Arial Black", sans-serif`;
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = Math.max(6, size * 0.12);
  ctx.strokeStyle = 'rgba(0,0,0,0.65)';
  ctx.strokeText(text, w / 2, h / 2 + size * 0.05);
  ctx.fillStyle = fg;
  ctx.fillText(text, w / 2, h / 2 + size * 0.05);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}
